import { Controller } from "@hotwired/stimulus"

// Re-labels server-rendered (Central Time) callback windows into the visitor's
// local timezone and records their selection into hidden fields. The server
// remains the source of truth: it re-validates the chosen window on submit.
export default class extends Controller {
  static targets = [
    "startInput", "endInput", "timezoneInput",
    "dayTab", "daySlots",
    "periodTab", "periodSlots",
    "slot", "summary", "businessHours"
  ]

  static SELECTED_CLASSES = ["bg-brand-blue-500", "text-white", "border-brand-blue-500"]
  static UNSELECTED_CLASSES = ["bg-white", "text-brand-gray-800", "border-brand-gray-300"]

  connect() {
    if (this.hasTimezoneInputTarget) {
      this.timezoneInputTarget.value = Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    this.relabelSlots()
    this.relabelDayTabs()
    this.relabelBusinessHours()
    this.restoreSelection()
  }

  selectDay(event) {
    this.showDay(Number(event.currentTarget.dataset.index))
  }

  // Roving-tabindex keyboard support for the day tablist (Left/Right/Home/End).
  onTabKeydown(event) {
    const tabs = this.dayTabTargets
    const current = tabs.indexOf(event.currentTarget)
    let next

    switch (event.key) {
      case "ArrowRight": next = (current + 1) % tabs.length; break
      case "ArrowLeft": next = (current - 1 + tabs.length) % tabs.length; break
      case "Home": next = 0; break
      case "End": next = tabs.length - 1; break
      default: return
    }

    event.preventDefault()
    this.showDay(Number(tabs[next].dataset.index))
    tabs[next].focus()
  }

  selectPeriod(event) {
    const { dayIndex, index } = event.currentTarget.dataset
    this.showPeriod(Number(dayIndex), Number(index))
  }

  // Roving-tabindex keyboard support for a day's period tablist (Left/Right/Home/End).
  onPeriodTabKeydown(event) {
    const dayIndex = Number(event.currentTarget.dataset.dayIndex)
    const tabs = this.periodTabTargets.filter((tab) => Number(tab.dataset.dayIndex) === dayIndex)
    const current = tabs.indexOf(event.currentTarget)
    let next

    switch (event.key) {
      case "ArrowRight": next = (current + 1) % tabs.length; break
      case "ArrowLeft": next = (current - 1 + tabs.length) % tabs.length; break
      case "Home": next = 0; break
      case "End": next = tabs.length - 1; break
      default: return
    }

    event.preventDefault()
    this.showPeriod(dayIndex, Number(tabs[next].dataset.index))
    tabs[next].focus()
  }

  selectSlot(event) {
    const selected = event.currentTarget

    this.slotTargets.forEach((slot) => this.deselect(slot))
    this.select(selected)

    this.startInputTarget.value = selected.dataset.start
    this.endInputTarget.value = selected.dataset.end
    this.updateSummary(selected.dataset.start, selected.dataset.end)
  }

  // After a failed submission the hidden fields keep the chosen window, but the
  // visual selection is JS state that resets on reconnect. Re-highlight the slot
  // (and reveal its day/period) so the user doesn't lose their pick. If that
  // window is no longer offered (e.g. the page sat open too long), fall back to
  // day one — the server will have flagged it as unavailable.
  restoreSelection() {
    const start = this.hasStartInputTarget ? this.startInputTarget.value : ""
    const slot = start && this.slotTargets.find((candidate) => candidate.dataset.start === start)

    if (!slot) {
      this.showDay(0)
      return
    }

    const dayGroup = this.daySlotsTargets.find((candidate) => candidate.contains(slot))
    this.showDay(dayGroup ? Number(dayGroup.dataset.index) : 0)

    const periodGroup = this.periodSlotsTargets.find((candidate) => candidate.contains(slot))
    if (periodGroup) this.showPeriod(Number(periodGroup.dataset.dayIndex), Number(periodGroup.dataset.index))

    this.select(slot)
    this.updateSummary(slot.dataset.start, slot.dataset.end)
  }

  showDay(index) {
    this.daySlotsTargets.forEach((group) => {
      group.classList.toggle("hidden", Number(group.dataset.index) !== index)
    })

    this.dayTabTargets.forEach((tab) => {
      const active = Number(tab.dataset.index) === index
      active ? this.select(tab) : this.deselect(tab)
      tab.setAttribute("aria-selected", active ? "true" : "false")
      tab.tabIndex = active ? 0 : -1
    })
  }

  showPeriod(dayIndex, index) {
    this.periodSlotsTargets.forEach((group) => {
      if (Number(group.dataset.dayIndex) !== dayIndex) return
      group.classList.toggle("hidden", Number(group.dataset.index) !== index)
    })

    this.periodTabTargets.forEach((tab) => {
      if (Number(tab.dataset.dayIndex) !== dayIndex) return

      const active = Number(tab.dataset.index) === index
      tab.classList.toggle("bg-white", active)
      tab.classList.toggle("shadow-sm", active)
      tab.classList.toggle("text-brand-gray-800", active)
      tab.classList.toggle("text-brand-gray-500", !active)
      tab.setAttribute("aria-selected", active ? "true" : "false")
      tab.tabIndex = active ? 0 : -1
    })
  }

  updateSummary(start, end) {
    if (!this.hasSummaryTarget) return

    this.summaryTarget.textContent = `${this.formatLongDate(start)} · ${this.formatTime(start)} – ${this.formatTime(end)}`
    this.summaryTarget.classList.remove("hidden")
  }

  relabelSlots() {
    this.slotTargets.forEach((slot) => {
      slot.textContent = this.formatTime(slot.dataset.start)
    })
  }

  relabelBusinessHours() {
    if (!this.hasBusinessHoursTarget) return

    const { start, end } = this.businessHoursTarget.dataset
    this.businessHoursTarget.textContent = `Weekdays · ${this.formatHour(start)} – ${this.formatHour(end)}`
  }

  relabelDayTabs() {
    this.dayTabTargets.forEach((tab) => {
      const date = new Date(tab.dataset.iso)
      const weekday = tab.querySelector("[data-role=weekday]")
      const day = tab.querySelector("[data-role=day]")
      if (weekday) weekday.textContent = date.toLocaleDateString([], { weekday: "short" })
      if (day) day.textContent = date.toLocaleDateString([], { day: "numeric" })
    })
  }

  select(element) {
    element.classList.remove(...this.constructor.UNSELECTED_CLASSES)
    element.classList.add(...this.constructor.SELECTED_CLASSES)
  }

  deselect(element) {
    element.classList.remove(...this.constructor.SELECTED_CLASSES)
    element.classList.add(...this.constructor.UNSELECTED_CLASSES)
  }

  formatTime(iso) {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
  }

  formatHour(iso) {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric" })
  }

  formatLongDate(iso) {
    return new Date(iso).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })
  }
}
